import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Music2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { STATIONS } from "../config/stations";
import StationChat from "../components/StationChat";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const StationPage = ({ stationId }) => {
  const params = useParams();
  const id = stationId || params.stationId;
  const station = STATIONS.find((s) => s.id === id);
  const { current, playing, play, toggle, muted, toggleMute, volume, setVolume } = usePlayer();
  const [nowPlaying, setNowPlaying] = useState(null);
  const [history, setHistory] = useState([]);
  const isCurrent = current?.id === id;
  const isLive = isCurrent && playing;

  const loadMeta = useCallback(async () => {
    if (!station) return;
    try {
      const [nRes, hRes] = await Promise.all([
        fetch(`${BACKEND}/api/now-playing/${id}`),
        fetch(`${BACKEND}/api/now-playing/${id}/history?limit=5`),
      ]);
      if (nRes.ok) setNowPlaying(await nRes.json());
      if (hRes.ok) setHistory(await hRes.json());
    } catch {}
  }, [id, station]);

  useEffect(() => {
    loadMeta();
    const t = setInterval(loadMeta, 15000);
    return () => clearInterval(t);
  }, [loadMeta]);

  if (!station) {
    return (
      <div className="max-w-6xl mx-auto px-5 md:px-10 text-center py-16">
        <h1 className="font-display text-4xl text-white uppercase">Station not found</h1>
      </div>
    );
  }

  const art = nowPlaying?.art || station.image;
  const title = nowPlaying?.title || "Live";
  const artist = nowPlaying?.artist || station.tagline;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
          <span className="relative flex w-2 h-2">
            {isLive && <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />}
            <span className={`relative inline-flex w-2 h-2 rounded-full ${isLive ? "bg-crimson-live live-dot" : "bg-gold"}`} />
          </span>
          <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
            {isLive ? "On Air Now" : "24 / 7 Live"} · {station.genre}
          </span>
        </div>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl leading-[0.9] text-white uppercase">
          {station.name}
        </h1>
        <p className="mt-3 font-body text-neutral-300 text-base md:text-lg">
          {station.tagline}
        </p>
      </header>

      {/* Now Playing + big artwork + chat */}
      <div className="grid lg:grid-cols-[1fr_400px] gap-6 mb-10">
        {/* Left: big artwork + track info */}
        <section
          data-testid={`station-artwork-${id}`}
          className="bg-jet-surface border border-gold/25 rounded-lg overflow-hidden"
        >
          <div className="relative aspect-square w-full max-w-[560px] mx-auto">
            <img
              src={art}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`station-art-img-${id}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
              <div className="font-condensed uppercase tracking-widest text-[11px] text-gold mb-1">
                Now Playing
              </div>
              <div className="font-display text-2xl md:text-3xl text-white uppercase leading-tight" data-testid={`station-track-title-${id}`}>
                {title}
              </div>
              {artist && (
                <div className="font-body text-base text-gold/90 mt-1" data-testid={`station-track-artist-${id}`}>
                  {artist}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 p-5 border-t border-neutral-900">
            <button
              data-testid={`station-play-${id}`}
              onClick={() => (isCurrent ? toggle() : play(id))}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors"
            >
              {isLive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isLive ? "Pause" : "Play Live"}
            </button>
            <button
              data-testid={`station-mute-${id}`}
              onClick={toggleMute}
              className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-4 py-3 rounded-full transition-colors"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              {muted ? "Unmute" : "Mute"}
            </button>
            <input
              data-testid={`station-volume-${id}`}
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={muted ? 0 : volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="accent-[#D4AF37] w-32"
            />
          </div>
        </section>

        {/* Right: per-station chat */}
        <StationChat room={id} title={`${station.name} · Chat`} />
      </div>

      {/* Recently played */}
      <section className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
          <Music2 className="w-4 h-4 text-gold" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Recently Played on {station.name}
          </h2>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 text-sm">
            No history yet. Tune in and this will fill up.
          </div>
        ) : (
          <ul className="divide-y divide-neutral-900">
            {history.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-neutral-900/40">
                <div className="w-10 h-10 shrink-0 rounded overflow-hidden border border-neutral-800 bg-black">
                  {t.art ? (
                    <img src={t.art} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div
                      className="w-full h-full bg-cover bg-center"
                      style={{ backgroundImage: `url(${station.image})` }}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-body text-sm text-white truncate">{t.title}</div>
                  <div className="font-body text-xs text-neutral-400 truncate">{t.artist || "—"}</div>
                </div>
                <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500 shrink-0">
                  {new Date(t.played_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default StationPage;
